import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tag, Users, TrendingUp, Award, MapPin, DollarSign, BookOpen, Grid3X3 } from 'lucide-react';
import { Link } from 'react-router-dom';
import {
  LandingHeroCarousel,
  LandingSponsorBanner,
  LandingTopCouponPreview,
  LandingPlans,
  LandingCouponsGrid,
  LandingGamification,
  LandingTestimonials,
  LandingTutorialPopup,
} from '../components/landing';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const HERO_IMAGE = "https://customer-assets.emergentagent.com/job_7a4550fd-9d62-4902-8879-0d259dfe4d1a/artifacts/bk22c5zq_CLUBDOCUPOM.png";

export default function LandingPage() {
  const [testimonials, setTestimonials] = useState([]);
  const [allCoupons, setAllCoupons] = useState([]);
  const [showAllCoupons, setShowAllCoupons] = useState(false);
  const [sponsorIndex, setSponsorIndex] = useState(0);
  const [banners, setBanners] = useState([]);
  const [heroCoupons, setHeroCoupons] = useState([]);
  const [heroIndex, setHeroIndex] = useState(0);

  useEffect(() => {
    Promise.all([
      axios.get(`${BACKEND_URL}/api/validations`).catch(() => ({ data: [] })),
      axios.get(`${BACKEND_URL}/api/featured-coupons`).catch(() => ({ data: [] })),
      axios.get(`${BACKEND_URL}/api/banners?tipo=global`).catch(() => ({ data: [] })),
      axios.get(`${BACKEND_URL}/api/schema/coupons`).catch(() => ({ data: null })),
      axios.get(`${BACKEND_URL}/api/hero-coupons`).catch(() => ({ data: [] }))
    ]).then(([testimonialsRes, featuredRes, bannersRes, schemaRes, heroRes]) => {
      setTestimonials(testimonialsRes.data.slice(0, 6));
      const coupons = featuredRes.data || [];
      setAllCoupons(coupons);
      setHeroCoupons(heroRes.data || []);
      setBanners(bannersRes.data || []);

      if (schemaRes.data) {
        const existing = document.getElementById('coupon-schema-ld');
        if (existing) existing.remove();
        const script = document.createElement('script');
        script.id = 'coupon-schema-ld';
        script.type = 'application/ld+json';
        script.textContent = JSON.stringify(schemaRes.data);
        document.head.appendChild(script);
      }
    });
  }, []);

  useEffect(() => {
    if (heroCoupons.length <= 1) return;
    const timer = setInterval(() => setHeroIndex(i => (i + 1) % heroCoupons.length), 2000);
    return () => clearInterval(timer);
  }, [heroCoupons]);

  useEffect(() => {
    if (banners.length === 0) return;
    const timer = setInterval(() => setSponsorIndex(i => (i + 1) % banners.length), 4000);
    return () => clearInterval(timer);
  }, [banners]);

  const handleLogin = () => {
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `${BACKEND_URL}/api/auth/google/login?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Navbar */}
      <nav className="bg-white/80 backdrop-blur-md border-b border-slate-200/50 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 sm:py-4 flex justify-between items-center">
          <span className="text-xl sm:text-2xl font-black text-slate-900">ClubdoCupom</span>
          <Button onClick={handleLogin}
            className="bg-amber-500 hover:bg-amber-600 text-white font-bold py-2 px-4 sm:px-6 rounded-lg shadow-md transition-all transform hover:-translate-y-0.5 hover:shadow-lg text-sm sm:text-base"
            data-testid="login-btn">
            Entrar
          </Button>
        </div>
      </nav>

      {/* Hero */}
      <section data-testid="hero-section">
        <div className="w-full">
          <img src={HERO_IMAGE} alt="ClubdoCupom" className="w-full h-auto object-cover max-h-[500px]" />
        </div>
        <div className="bg-slate-900 py-4 sm:py-6">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center sm:text-left">
            <p className="text-sm sm:text-lg text-white/90 mb-3" data-testid="hero-text">Faça login de forma segura para acessar o app</p>
            <Button onClick={handleLogin} size="lg"
              className="bg-amber-500 hover:bg-amber-600 text-white font-bold py-2.5 px-6 sm:py-3 sm:px-8 rounded-lg shadow-lg transition-all transform hover:-translate-y-0.5 hover:shadow-xl text-sm sm:text-lg w-auto"
              data-testid="hero-cta-btn">
              Comece Agora
            </Button>
          </div>
        </div>
      </section>

      <LandingSponsorBanner banners={banners} sponsorIndex={sponsorIndex} setSponsorIndex={setSponsorIndex} />
      <LandingHeroCarousel heroCoupons={heroCoupons} heroIndex={heroIndex} setHeroIndex={setHeroIndex} onLogin={handleLogin} />

      {/* Explorar por Categoria */}
      <section className="py-10 md:py-14" data-testid="explore-categories-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-slate-900 mb-3">Explore por Categoria</h2>
            <p className="text-base text-slate-600">Encontre cupons no segmento que você procura</p>
          </div>
          <div className="flex flex-wrap justify-center gap-3">
            {[
              { name: 'Gastronomia', slug: 'gastronomia-e-bebidas' },
              { name: 'Beleza Feminina', slug: 'beleza-feminina' },
              { name: 'Saúde', slug: 'saude' },
              { name: 'Tecnologia', slug: 'eletronicos-e-tecnologia' },
              { name: 'Casa e Decoração', slug: 'casa-e-decoracao' },
              { name: 'Moda', slug: 'moda-e-acessorios' },
              { name: 'Pet Shop', slug: 'pet-shop' },
              { name: 'Construção', slug: 'construcao-e-reforma' },
              { name: 'Educação', slug: 'educacao-e-ensino' },
            ].map(cat => (
              <Link key={cat.slug} to={`/categorias/${cat.slug}`} data-testid={`landing-cat-${cat.slug}`}>
                <Badge className="bg-white border-slate-200 text-slate-700 hover:bg-amber-50 hover:border-amber-300 hover:text-amber-700 transition-all px-4 py-2 text-sm cursor-pointer shadow-sm">
                  {cat.name}
                </Badge>
              </Link>
            ))}
          </div>
          <div className="text-center mt-6">
            <Link to="/categorias" data-testid="view-all-categories-btn">
              <Button variant="outline" className="border-slate-400 text-slate-700 hover:bg-slate-100 font-semibold">
                <Grid3X3 className="h-4 w-4 mr-2" /> Ver Todas as Categorias
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Como Funciona */}
      <section className="py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-slate-900 mb-4">Como Funciona</h2>
            <p className="text-base text-slate-600 max-w-2xl mx-auto">Sistema simples e direto para economizar com descontos exclusivos</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { icon: Tag, title: 'Escolha o Cupom', desc: 'Navegue por centenas de cupons de desconto organizados por categoria, cidade e estado.' },
              { icon: Users, title: 'Receba no WhatsApp', desc: 'Ao clicar, receba o código e contato do lojista automaticamente no seu WhatsApp.' },
              { icon: Award, title: 'Valide e Ganhe', desc: 'Após a compra, valide e deixe seu depoimento para ganhar pontos e acessar mais cupons.' }
            ].map((item, i) => (
              <div key={`feature-${i}`} className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm" data-testid={`feature-card-${i + 1}`}>
                <div className="bg-amber-50 w-12 h-12 rounded-lg flex items-center justify-center mb-4">
                  <item.icon className="h-6 w-6 text-amber-600" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-3">{item.title}</h3>
                <p className="text-base text-slate-600">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <LandingTopCouponPreview heroCoupons={heroCoupons} onLogin={handleLogin} />

      {/* Seja Influenciador */}
      <section className="py-12 md:py-16 bg-gradient-to-br from-indigo-50 to-purple-50" data-testid="become-influencer-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Badge className="bg-indigo-100 text-indigo-700 border-indigo-200 mb-3">Oportunidade</Badge>
          <h2 className="text-2xl md:text-3xl font-black text-slate-900 mb-4">Seja Influenciador do ClubdoCupom</h2>
          <div className="space-y-3 mb-6">
            {[
              { icon: BookOpen, text: 'Treinamento gratuito com dicas de marketing e estrategia' },
              { icon: DollarSign, text: 'Aprenda a monetizar no ClubdoCupom' },
              { icon: TrendingUp, text: 'Dashboard exclusivo para impulsionar seus clientes' },
              { icon: Users, text: 'Gerencie sua carteira de lojistas com facilidade' }
            ].map((item, i) => (
              <div key={`inf-feat-${i}`} className="flex items-center gap-3">
                <div className="bg-indigo-100 p-1.5 rounded-md"><item.icon className="h-4 w-4 text-indigo-600" /></div>
                <p className="text-slate-700">{item.text}</p>
              </div>
            ))}
          </div>
          <Button onClick={handleLogin} size="lg"
            className="bg-indigo-600 hover:bg-indigo-700 text-white font-bold px-8 py-3 rounded-lg shadow-md transition-all transform hover:-translate-y-0.5 text-lg"
            data-testid="become-influencer-btn">
            Comece Agora
          </Button>
        </div>
      </section>

      <LandingPlans onLogin={handleLogin} />
      <LandingCouponsGrid allCoupons={allCoupons} showAllCoupons={showAllCoupons} setShowAllCoupons={setShowAllCoupons} onLogin={handleLogin} />
      <LandingGamification />
      <LandingTestimonials testimonials={testimonials} />
      <LandingTutorialPopup />

      {/* CTA */}
      <section className="py-16 md:py-20 hero-gradient">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-white mb-6">Pronto para Economizar?</h2>
          <p className="text-base text-slate-300 mb-8">Junte-se a milhares de consumidores e lojistas</p>
          <Button onClick={handleLogin} size="lg"
            className="bg-amber-500 hover:bg-amber-600 text-white font-bold py-4 px-10 rounded-lg shadow-xl transition-all transform hover:-translate-y-0.5 hover:shadow-2xl text-lg"
            data-testid="cta-btn">
            Criar Conta Grátis
          </Button>
        </div>
      </section>
    </div>
  );
}
